/**
 * executor.ts
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */

import bluebird from "bluebird";

import { ResolveFn } from "./constants";
import { ErrorCode, GroupError } from "./error";
import { PriorityQueue } from "./priorityQueue";

type dispatchFn<T> = (() => T) | (() => Promise<T>);

interface Job<T, S> {
  obj: T,
  fn: dispatchFn<S>,
  resolver: ResolveFn<S>
  promise: Promise<S>
}

export enum ErrorInstruction {
  stopQueue = 0,
  shouldContinue = 1,
  continueWithoutCompleting = 3
}

export class Executor<T> {
  private _id: string;
  private queue: PriorityQueue<Job<T, any>>;
  private idle: boolean;
  private finishPromise: Promise<T | undefined> | null;
  private errorCB: (_id: string, err: Error, executor: Executor<T>) => Promise<ErrorInstruction>

  public metadData: any;

  get length(): number {
    return this.queue.length;
  }

  constructor (_id: string, comparator: (a: T, b: T) => boolean,
        errorCB: (_id: string, err: Error, executor: Executor<T>) => Promise<ErrorInstruction>) {
    this._id = _id;
    this.idle = true;
    this.queue = new PriorityQueue<Job<T, any>>(
      (a: Job<T, any>, b: Job<T, any>) => comparator(a.obj, b.obj));
    this.finishPromise = null;
    this.errorCB = errorCB;
  }

  add<R> (value: T, fn: dispatchFn<R>): Promise<R> {
    // this is kind of funky, but we create and return a promise
    // representing the completion of fn() here. But we need to
    // save the resolve() function of that promise for when we
    // actually execute fn() later in time.
    const job: Partial<Job<T,R>> = {
      obj: value,
      fn: fn
    };
    job.promise = new bluebird<R>(resolve => {
      job.resolver = resolve;
    });
    this.queue.enqueue(job as Job<T, R>);
    return job.promise as Promise<R>;
  }

  addAndStart<R> (value: T, fn: dispatchFn<R>): Promise<R> {
    const job: Partial<Job<T,R>> = {
      obj: value,
      fn: fn
    };
    job.promise = new bluebird<R>(resolve => {
      job.resolver = resolve;
    });
    this.queue.enqueue(job as Job<T, R>);
    if (this.idle) {
      this.finishPromise = this.processJobs();
    }
    return job.promise as Promise<R>;
  }

  start(): Promise<T | undefined> {
    if (this.idle) {
      this.finishPromise = this.processJobs();
    } else {
      console.log("already started? idle:", this.idle, this.finishPromise);
    }
    return this.finishPromise as Promise<T>;
  }

  clear(): void {
    this.queue.empty();
    this.idle = true;
    this.finishPromise = null;
  }

  join(): Promise<T | undefined> | null {
    return this.finishPromise;
  }

  private async processJobs (): Promise<T | undefined> {
    this.idle = false;
    let job: Job<T, any> | undefined;
    let lastObj: T | undefined = undefined;
    let continueCompleting = true;

    while ((job = this.queue.dequeue())) {
      try {
        //console.log("running job with priority:", job.obj);
        job.resolver(await job.fn()); // await blocks loop until job completes
        if (continueCompleting) {
          lastObj = job.obj;
        }
      } catch (err) {
        // console.error("Uncaught Error processing executor job!!", err);
        const errCmd = await this.errorCB(this._id, err as Error, this);
        job.resolver(bluebird.reject(err));
        if (err instanceof GroupError && err.code === ErrorCode.continueAck && continueCompleting) {
          lastObj = job.obj;
        }
        if (errCmd === ErrorInstruction.stopQueue) {
          break; // can't use a switch statement because of this
        } else if (errCmd === ErrorInstruction.continueWithoutCompleting) {
          continueCompleting = false;
        }
      }
    }

    this.idle = true;
    return lastObj;
  }
}
